/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align */

import {ProjectElement}          from './project.element';
import {ProjectElementType}      from './project.element.type';
import { ProjectElementFactory } from '../project.element.factory';

import { Project }               from '../project';

import { BlobType }              from '../blob';
import { Logger }                from '@/util/logger';

import {ActionType} from "@/models/action";
import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";
import {ActionService} from "@/services/action.service";
import {ServiceLocator} from "@/services/service.locator";

/**
 *
 * @class SimulationGroupElement
 *
 * @brief root element for a simulation, can have inputs and results.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
@ProjectElementFactoryDecorator('SimulationGroupElement')
export class SimulationGroupElement extends ProjectElement {

  public actionKeys: string[] = ['simulate', 'clonesimulation',  'renamesimulation', 'deletesimulation', 'generatekpitable'];

  /** standard constructor */
  constructor(
    project: Project,
         id: string,
       name: string,
     parent: ProjectElement,
    publish?: boolean
  ) {

    super(project, id, ProjectElementType.SimulationGroup);

    this._acceptsElements.push(ProjectElementType.Inputs, ProjectElementType.Results);
    this._acceptsBlobs.push(BlobType.None);

    this._enabled     = true;
    this._label       = name;
    this._icon        = 'fas fa-flag-checkered';
    this._elementKind = 'Simulation Group';

    if (parent) {

      this._project.addElement(this, parent, publish);

      ProjectElementFactory.fromObject(ProjectElementType.Inputs, {
        project: this._project,
        id:      '',
        parent:  this,
        publish
      });

      ProjectElementFactory.fromObject(ProjectElementType.Results, {
        project: this._project,
        id:      '',
        parent:  this,
        publish
      });

    }

  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring SimulationGroupElement");
  }

  /** order the child array if there is ordering to the children */
  public orderChildren() {

    if (this._children.length === 0) {

      return;
    }

    let results: ProjectElement[] = [];

    const i   = this._children.find(x => x._type === ProjectElementType.Inputs);
    const exi = this._children.filter(x => x._type !== ProjectElementType.Inputs);

    results.push(i);
    results = results.concat(exi);

    this._children = results;
  }

  /**
   * sub-classes must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.simulate = {
      name: 'Simulate',
      icon: 'fas fa-play',
      disabled: false
    };

    actionSet.clonesimulation = {
      name: 'Clone Simulation',
      icon: 'fas fa-clone',
      disabled: this.isBusy()
    };

    actionSet.generatekpitable = {
      name: 'Generate KPI Table',
      icon: 'far fa-chart-bar',
      disabled:  this.isBusy()
    };

    actionSet.renamesimulation = {
      name: 'Rename Simulation',
      icon: 'fas fa-tag',
      disabled:  this.isBusy()
    };

    actionSet.deletesimulation = {
      name: 'Delete Simulation',
      icon: 'fas fa-trash-alt',
      disabled:  this.isBusy()
    };

    return actionSet;
  }

  /** is this element deletable? */
  public isDeletable() : boolean {

    return !this.isBusy();
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.actionKeys.find(x => x === key);
  }

  /** subclasses must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    switch (key) {

      case 'generatekpitable': {

        this._project.generateKPITable(this);
      }
      break;

      case 'simulate': {

        Logger.debug('[SimulationGroupElement] invoking simulate action...');

        /* simulation action can be invoked via the simulation button and the simulation action */

        const actions: ActionService = ServiceLocator.injector.get(ActionService);

        actions.action(ActionType.Window_SimpleSim).execute();
      }
      break;

      case 'clonesimulation': {

        Logger.debug('[SimulationGroupElement] clonesimulation');

      }
      break;

      case 'renamesimulation': {

        Logger.debug('[SimulationGroupElement] re-naming');

        /* simulation groups are just logical containers, there is no blob key changes */

        const ag: SimulationGroupElement = this;

        ag._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            Logger.debug('[SimulationGroupElement] validating: ', name);

            /* is it a valid name? */

            if (name.trim() === '') {

              reject('Simulation name name can not be empty.');

              return ;
            }

            /* make sure we aren't name clashing ...  */

            const peers: ProjectElement[] = ag._parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Simulation of that name already exists.');

                return ;
              }

            }

            /* its ok to use */

            resolve(name);

          },
          (name) => {

            /* do the actual renaming... */

            Logger.debug('[SimulationGroupElement] renaming... ',  name);

            /* figure out what the path changes are ... */

            const       parents: ProjectElement[] = this.ancestors().reverse();
            parents.shift();
            const  search: string = ag._project.dirname() +
              '/' +
              parents.map(x => x._label).join('/') + `/${this._label}`;

            const replace: string = ag._project.dirname() +
              '/' +
              parents.map(x => x._label).join('/') + `/${name}`;

            Logger.debug(`[SimulationGroupElement] renaming... ${search} => ${replace}...`);

            /* adjust (in bulk)... */

            this.shiftBlobs(search, replace, (result) => {

              if (result !== true) {

                /* there was a problem! */

                Logger.error(`[SimulationGroupElement] could not properly rename children: `, result);

                return ;
              }

              Logger.debug('[SimulationGroupElement] children re-named, saving...');

              ag._label = name;

              ag.redraw();

              /* trigger a save of the project */

              ag.save();

            });

          }, {
            title: 'Rename Simulation',
            placeholder: this._label
          });

      }
      break;

      case 'deletesimulation': {

        Logger.debug('[SimulationGroupElement] deletesimulation');

        this.deleteElements(selection);

      }
      break;

    }
  }

}
